import type { ClaimStatus, GbStatus, GlassPosition } from "./enums.js";

// Display labels for the claim vocabularies, and the reverse: free text from the sheet or a GHL
// stage name back to the enum. Shared by the web app, the GHL adapter, the webhook and the import.

export const GLASS_LABEL: Record<GlassPosition, string> = {
  windshield: "Windshield",
  back_glass: "Back glass",
  door_front_left: "Front door L",
  door_front_right: "Front door R",
  door_rear_left: "Rear door L",
  door_rear_right: "Rear door R",
  quarter_left: "Quarter L",
  quarter_right: "Quarter R",
  vent_left: "Vent L",
  vent_right: "Vent R",
  sunroof: "Sunroof",
};

/** "Windshield, Rear door L": the one way glass reads in GHL, email and the GlassBiller packet. */
export const glassText = (positions: readonly GlassPosition[]) => positions.map((p) => GLASS_LABEL[p]).join(", ");

export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  new: "New",
  pending_schedule: "Pending Schedule",
  scheduled: "Scheduled",
  installed: "Installed",
  needs_rescheduled: "Needs Rescheduled",
  call_later: "Call Later",
  no_contact: "No Contact",
  three_day_no_contact: "3 Day No Contact",
  inspection: "Inspection",
  pending_coverage: "Pending Coverage",
  pending_approval: "Pending Approval",
  no_coverage: "No Coverage",
  oem_requested: "OEM Requested",
  repair: "Repair",
  repair_loop: "Repair Loop",
  cash_quote_pending: "Cash Quote Pending",
  cash_quote_scheduled: "Cash Quote Scheduled",
  no_damage: "No Damage",
  cancelled: "Cancelled",
  duplicate: "Duplicate",
};

export const GB_STATUS_LABEL: Record<GbStatus, string> = {
  needs_built: "Needs Built",
  built: "Built",
  not_built: "Not Built",
  pending_contact: "Pending Contact",
  pending_dispatch: "Pending Dispatch",
  cash_quote: "Cash Quote",
  cancelled: "Cancelled",
  void: "Void",
  duplicate: "Duplicate",
};

/** Sheet "Scheduled" values that read as follow-up work for the office (the Follow up tabs). */
export const FOLLOW_UP_STATUSES: readonly ClaimStatus[] = [
  "new", "pending_schedule", "needs_rescheduled", "call_later", "no_contact", "inspection",
  "pending_coverage", "pending_approval", "oem_requested", "repair", "repair_loop", "cash_quote_pending",
];

/** Claims that ended without an install. */
export const CLOSED_LOST_STATUSES: readonly ClaimStatus[] = [
  "three_day_no_contact", "no_coverage", "no_damage", "cancelled", "duplicate",
];

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const reverse = <T extends string>(labels: Record<T, string>, extra: Record<string, T>) => {
  const map = new Map<string, T>();
  for (const [key, label] of Object.entries(labels) as [T, string][]) {
    map.set(squash(key), key);
    map.set(squash(label), key);
  }
  for (const [k, v] of Object.entries(extra)) map.set(squash(k), v);
  return (raw: string | undefined | null): T | undefined => (raw ? map.get(squash(String(raw))) : undefined);
};

/** "3 Day No Contact", "three_day_no_contact", "Cash quote pending" -> the enum. Unknown -> undefined. */
export const parseClaimStatus = reverse(CLAIM_STATUS_LABEL, {
  "3daynocontact": "three_day_no_contact",
  "nodamage": "no_damage",
  "canceled": "cancelled",
});

export const parseGbStatus = reverse(GB_STATUS_LABEL, { voi: "void", cashquote: "cash_quote" });
